import {Survey, SurveyElementChoices} from 'core/SurveyModels'

export class SurveyTranslateChangesService {
  private _log = new Set<string>()
  private readonly choiceMatch = new RegExp(/\[\d](?=\.)/)

  private constructor() {}

  private findIndexes(
    survey: Survey,
    pageName: string,
    elementName: string,
    choice: string
  ): [number, number, number | undefined] {
    const pageIndex = survey.pages.findIndex((p) => p.name === pageName)
    const elementIndex = survey.pages[pageIndex].elements.findIndex(
      (e) => e.name === elementName
    )
    let choiceIndex = undefined
    if (choice) {
      const element = survey.pages[pageIndex].elements[
        elementIndex
      ] as SurveyElementChoices
      choiceIndex = element.choices?.findIndex((c) => c.value === choice)
    }
    return [pageIndex, elementIndex, choiceIndex]
  }

  // Log that are relevant to the survey
  private validate(survey: Survey) {
    return Array.from(this._log).filter((log) => {
      const [pageName, elementName, prop, choice] = log.split('.')
      const [pageIndex, elementIndex, choiceIndex] = this.findIndexes(
        survey,
        pageName,
        elementName,
        choice
      )
      const element: unknown =
        survey.pages?.[pageIndex]?.elements?.[elementIndex]

      if (choiceIndex) {
        return (element as SurveyElementChoices)?.choices?.[choiceIndex]
      }

      return (element as Record<string, unknown>)[prop] as string
    })
  }

  log(survey: Survey, pageName: string, elementName: string, propPath: string) {
    let path = propPath
    if (
      !path ||
      (propPath?.includes('choices') && !this.choiceMatch.test(path))
    ) {
      return
    }
    if (this.choiceMatch.test(path)) {
      const choiceIndex = this.choiceMatch.exec(path)![0].replace(/\D/g, '')
      const pageIndex = survey.pages.findIndex((p) => p.name === pageName)
      const elementIndex = survey.pages[pageIndex].elements.findIndex(
        (e) => e.name === elementName
      )
      const element = survey.pages[pageIndex].elements[
        elementIndex
      ] as SurveyElementChoices
      const choiceId = element.choices![parseInt(choiceIndex)].value
      path = path
        .replace(this.choiceMatch, `.${choiceId}`)
        .replace('.label', '')
    }
    path = `${pageName}.${elementName}.${path.replace(/\..{2}$/, '')}`
    this._log.add(path)
  }

  hasChanges(survey: Survey) {
    return this.validate(survey).length > 0
  }

  getLog(survey: Survey) {
    return this.validate(survey)
  }

  clearLog() {
    this._log = new Set()
  }

  free() {
    SurveyTranslateChangesService.instance = undefined
  }

  private static instance?: SurveyTranslateChangesService

  static shared() {
    if (!SurveyTranslateChangesService.instance) {
      SurveyTranslateChangesService.instance =
        new SurveyTranslateChangesService()
    }
    return SurveyTranslateChangesService.instance
  }
}
